import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetsService } from '../../assets.service';
import { IpfsService } from '../../ipfs.service';
import { BehaviorSubject, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MessagingService } from '../../messaging.service';

// Interfaces matching the new policy structure
interface Condition {
  subject: 'Sender' | 'Receiver';
  name: string;
  predicate: string;
  value: string | number | string[];
  issuers?: string[];
}

interface ConditionGroup {
  description?: string;
  composition: 'ALL' | 'ANY';
  conditions: Condition[];
}

interface Policy {
  name?: string;
  description?: string;
  trustedIssuers: string[];
  permanent: boolean;
  groupComposition: 'ALL' | 'ANY';
  groups: ConditionGroup[];
}

// Interface for the JSON structure stored on IPFS
interface PolicyJson {
  name?: string;
  description?: string;
  trustedIssuers?: string[];
  permanent?: boolean;
  nextConditionHash?: string;
  groupComposition: 'ALL' | 'ANY';
  groups: ConditionGroup[];
}

@Component({
  selector: 'ssi-transaction-result',
  templateUrl: './transaction-result.component.html',
  styleUrls: ['./transaction-result.component.scss'],
})
export class TransactionResultComponent {
  result: string = '';
  txHash: string = '';
  isMining: boolean = false;
  isUploadingPolicies: boolean = false;
  verificationNeeded: boolean = false;
  verificationInitiated: string | undefined;
  senderCredentialStatus: string | undefined;
  receiverCredentialStatus: string | undefined;
  txAuthorized: string | undefined;
  errorMessage: string = '';

  // Track policy upload progress
  policyUploadStatuses: string[] = [];
  createdPolicyHashes: string[] = [];

  timerSrc: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  timerStop: Subject<any> = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TransactionResultComponent>,
    private assetsService: AssetsService,
    private ipfsService: IpfsService,
    private messagingService: MessagingService
  ) {
    this.transfer(this.data);
  }

  async close(): Promise<void> {
    this.dialogRef.close(this.result === 'successful');
  }

  async transfer(data: any): Promise<void> {
    const id = data.id;
    const to = this.assetsService.getAddress(data.to);
    const amount = data.amount;
    const policies: Policy[] = data.policies || [];

    let txReceipt;

    try {
      if (id === '0x0000000000000000000000000000000000000000000000000000000000000000') {
        // Transferring unrestricted tokens (ZERO_TOKEN)
        this.verificationNeeded = false;

        if (policies.length === 0) {
          // Simple transfer without conditions
          txReceipt = await (await this.transferAssetWithoutConditions(to, id, amount)).wait();
        } else {
          // Transfer with new policy structure
          txReceipt = await (await this.setConditionsFromPolicies(to, amount, policies)).wait();
        }
        this.interpretTxReceipt(txReceipt);
      } else {
        // Transferring policy-bound tokens - need verification
        this.verificationNeeded = true;
        await this.transferAssetWithConditions(to, id, amount);
      }
    } catch (e: any) {
      console.error('Transfer error:', e);
      this.verificationNeeded = false;
      this.isUploadingPolicies = false;
      this.result = 'failed';
      this.txHash = '';
      this.errorMessage = e.message || 'Unknown error occurred';
    }
  }

  /**
   * Convert Policy objects to JSON and upload to IPFS, then call smart contract

   */
  async setConditionsFromPolicies(to: string, amount: number, policies: Policy[]): Promise<any> {
    this.isUploadingPolicies = true;
    this.policyUploadStatuses = [];
    this.createdPolicyHashes = [];

    const wallet = this.assetsService.getWalletWithProvider();
    const walletAddr = await wallet.getAddress();

    // Check if the last policy is permanent
    const lastIsPermanent = policies.length > 0 && policies[policies.length - 1].permanent === true;

    // Process policies in reverse order to build the chain
    const tokenURIs: string[] = [];
    let nextConditionHash: string | undefined = undefined;

    for (let i = policies.length - 1; i >= 0; i--) {
      const policy = policies[i];
      this.policyUploadStatuses.unshift(`Uploading Policy Layer ${i + 1}...`);

      // Build the JSON structure for IPFS
      const policyJson: PolicyJson = {
        groupComposition: policy.groupComposition,
        groups: policy.groups,
      };

      // Add optional fields if present
      if (policy.name && policy.name.trim()) {
        policyJson.name = policy.name;
      }
      if (policy.description && policy.description.trim()) {
        policyJson.description = policy.description;
      }
      if (policy.trustedIssuers && policy.trustedIssuers.length > 0) {
        policyJson.trustedIssuers = policy.trustedIssuers;
      }

      // Handle chaining and permanent flag
      if (i === policies.length - 1) {
        // Last policy in the chain
        if (policy.permanent) {
          policyJson.permanent = true;
        }
        // No nextConditionHash for the last policy
      } else {
        // Not the last policy - add reference to next policy's hash
        if (nextConditionHash) {
          policyJson.nextConditionHash = nextConditionHash;
        }
      }

      // Upload to IPFS
      const ipfsResult = await this.ipfsService.setMeta(policyJson);

      // Validate that we got a valid hash back
      if (!ipfsResult || typeof ipfsResult !== 'string') {
        throw new Error(`Failed to upload Policy Layer ${i + 1} to IPFS`);
      }

      const ipfsHash: string = ipfsResult;
      console.log(`Policy Layer ${i + 1} uploaded with hash: ${ipfsHash}`);

      // Store for display and for chaining
      tokenURIs.unshift(ipfsHash);
      nextConditionHash = ipfsHash;

      this.policyUploadStatuses[0] = `Policy Layer ${i + 1} uploaded: ${ipfsHash.substring(0, 16)}...`;
      this.createdPolicyHashes.unshift(ipfsHash);
    }

    this.policyUploadStatuses.push('All policies uploaded. Submitting transaction...');
    this.isUploadingPolicies = false;
    this.isMining = true;

    // Call the smart contract with the permanent flag
    // Use the new function signature with lastIsPermanent parameter
    return await this.assetsService.call(
      'pm',
      'setSpendingConditions(address,address,bytes32[],uint256,bool)',
      [walletAddr, to, tokenURIs, amount, lastIsPermanent]
    );
  }

  async transferAssetWithoutConditions(to: string, id: string, amount: number): Promise<any> {
    const wallet = this.assetsService.getWalletWithProvider();
    const walletAddr = await wallet.getAddress();
    this.isMining = true;
    return await this.assetsService.call(
      'pm',
      'safeTransferFrom',
      [walletAddr, to, id, amount]
    );
  }

  async transferAssetWithConditions(to: string, id: string, amount: number): Promise<void> {
    const wallet = this.assetsService.getWalletWithProvider();
    const walletAddr = await wallet.getAddress();
    this.verificationInitiated = 'Initiating verification...';

    const result: any = await this.messagingService.initiateTransaction(walletAddr, to, id);
    let sessionId: string;

    if (result.sessionId) {
      this.verificationInitiated = 'Verification process is initiated.';
      sessionId = result.sessionId;
    } else {
      throw new Error('Initiation failed');
    }

    let current = new Date();
    const startTimestamp = current.getTime();
    console.log('Starting querying OVO agent...');

    this.timerSrc
      .asObservable()
      .pipe(
        takeUntil(this.timerStop),
        switchMap((time: number) => timer(time)),
        switchMap(() => this.messagingService.querySession(sessionId))
      )
      .subscribe({
        next: (response: any) => {
          try {
            switch (response['status']['txStatus']) {
              // auth tx idle
              case 0:
                if ('sender' in response['status']) {
                  switch (response['status']['sender']) {
                    case 'waiting':
                      this.senderCredentialStatus = 'Waiting for sender credentials...';
                      break;
                    case 'verified':
                      this.senderCredentialStatus = 'Sender credentials verified.';
                      break;
                    case 'error':
                      throw new Error('Sender credential verification failed');
                    default:
                      break;
                  }
                }
                if ('receiver' in response['status']) {
                  switch (response['status']['receiver']) {
                    case 'waiting':
                      this.receiverCredentialStatus = 'Waiting for receiver credentials...';
                      break;
                    case 'verified':
                      this.receiverCredentialStatus = 'Receiver credentials verified.';
                      break;
                    case 'error':
                      throw new Error('Receiver credential verification failed');
                    default:
                      break;
                  }
                }
                break;
              // auth tx pending
              case 1:
                if ('sender' in response['status'])
                  this.senderCredentialStatus = 'Sender credentials verified.';
                if ('receiver' in response['status'])
                  this.receiverCredentialStatus = 'Receiver credentials verified.';
                this.txAuthorized = 'Waiting for authorization...';
                break;
              // auth tx successful
              case 2:
                this.txAuthorized = 'Authorization completed.';
                this.timerStop.next(0);
                // Call the method without returning
                this.makeTransferTx(walletAddr, to, id, amount);
                break;
              // auth tx declined
              case 3:
                this.timerStop.next(0);
                throw new Error('Authorization declined');
            }

            current = new Date();
            const currentTimestamp = current.getTime();
            if (currentTimestamp - startTimestamp > environment.authExpire) {
              this.timerStop.next(0);
              throw new Error('Authentication time out');
            } else {
              this.timerSrc.next(2000); // call again in 2 seconds
            }
          } catch (e: any) {
            this.verificationNeeded = false;
            console.error('Verification error:', e);
            this.result = 'failed';
            this.txHash = '';
            this.errorMessage = e.message || 'Verification failed';
            this.isMining = false;
          }
        },
        error: (error: any) => {
          console.error('Query error:', error);
          this.verificationNeeded = false;
          this.result = 'failed';
          this.errorMessage = error.message || 'Query failed';
          this.isMining = false;
        },
      });
  }

  async makeTransferTx(walletAddr: string, to: string, id: string, amount: number): Promise<void> {
    this.verificationNeeded = false;
    this.isMining = true;
    console.log('Authorization successful, making transfer tx...');
    try {
      const txReceipt = await (
        await this.assetsService.call('pm',
          'safeTransferFrom',
        [walletAddr, to, id, amount]
      )).wait();
      this.interpretTxReceipt(txReceipt);
    } catch (e: any) {
      console.error('Transfer execution error:', e);
      this.result = 'failed';
      this.txHash = '';
      this.errorMessage = e.message || 'Transfer execution failed';
      this.isMining = false;
    }
  }

  interpretTxReceipt(txReceipt: { status: number; hash: string }): void {
    this.result = txReceipt.status ? 'successful' : 'failed';
    this.txHash = txReceipt.hash;
    this.isMining = false;
  }
}
